<template>
  <div class="flex-grow basis-3/4 overflow-y-auto p-[25px] scrollbar-thumb-[#E5E5E5] [&::-webkit-scrollbar-thumb]:rounded-full">
    <!-- Render messages here -->
    <div v-for="(msg, i) in messages" :key="i" class="mb-2">
      <div v-if="msg.messageType === 'unread_divider'" class="flex items-center my-[15px]">
        <span class="flex-grow h-px bg-[#E5E5E5]"></span>
        <span class="px-[12px] text-xs text-[#9A9A9A] whitespace-nowrap">Mensajes no leídos</span>
        <span class="flex-grow h-px bg-[#E5E5E5]"></span>
      </div>
      <div class="text-right" v-else-if="msg.sender === 'user'">
        <span class="bg-[#F2F2F2] p-[20px] rounded-[15px] text-[#2E2E2E] text-left inline-block max-w-[75%]">
          {{ msg.messageText }}
        </span>
        <div v-if="msg.createdAt" class="text-[11px] text-[#9A9A9A] mt-[4px] mb-[20px]">
          {{ formatMessageDate(msg.createdAt) }}
        </div>
      </div>
      <div class="text-left" v-else-if="msg.messageType === 'text'">
        <div
          class="markdown-body inline-block max-w-[75%] text-[#2E2E2E] text-left mb-[10px]"
          v-html="renderMarkdown(msg.messageText)"
        ></div>
        <div v-if="msg.createdAt" class="text-[11px] text-[#9A9A9A] mb-[6px]">
          {{ formatMessageDate(msg.createdAt) }}
        </div>
        <div class="flex mb-[20px]">
          <button
            @click="$emit('feedback', msg, true)"
            class="bg-transparent border-transparent"
          >
            <ThumbsUpIcon :active="msg.rating === 5" />
          </button>
          <button
            @click="$emit('feedback', msg, false)"
            class="bg-transparent border-transparent"
          >
            <ThumbsDownIcon :active="msg.rating === 1" />
          </button>
        </div>
        <div v-if="this.debug && (msg.metadata != null && msg.metadata != undefined && msg.metadata[0] !== null && msg.metadata[0] !== undefined)"
          class="max-w-[75%] text-[#2E2E2E] text-left mb-[10px] bg-[#D8D8D8] p-[20px] rounded-[15px]">
          <p>Metada:</p>
          <p>- Intencion: {{msg.metadata[0]?.intent}}</p>
          <span v-if="msg.metadata[0]?.intent === 'data' || msg.metadata[0]?.intent === 'count'">
            <p>- Enfoque: {{msg.metadata[0]?.enfoque}}</p>
            <p>- Cuestionario: {{msg.metadata[0]?.questionary}}</p>
            <p>- Metrica: {{msg.metadata[0]?.metric}}</p>
            <p class="whitespace-pre-wrap break-words">- Filtros: {{msg.metadata[0]?.filters}}</p>
            <p>- Data obtenida: {{msg.metadata[0]?.dataUsedForMessage}}</p>
          </span>
        </div>
      </div>
      <div v-else-if="msg.messageType === 'status'">
        <div class="text-sm mb-2">
          <span v-if="!msg.completed">
            🔄 {{ msg.status }}
          </span>

          <span v-else>
            ✅ {{ msg.status }}
          </span>
        </div>
      </div>
      <div v-else-if="msg.messageType === 'interactive'">
        <ChatInteractive :msg="msg" @answer="$emit('answer', $event)" />
        <div v-if="msg.createdAt" class="text-[11px] text-[#9A9A9A] mb-[10px]">
          {{ formatMessageDate(msg.createdAt) }}
        </div>
      </div>

      <!-- Inline chart/image or file download (independent of message type; a message may have text and/or a file) -->
      <div v-if="msg.sender !== 'user' && imageRawUrl(msg)" class="text-left mb-[20px]">
        <template v-if="!isFailed(imageRawUrl(msg))">
          <img
            :src="buildUrl(imageRawUrl(msg))"
            loading="lazy"
            alt="Gráfico"
            class="max-w-[75%] rounded-[15px] cursor-zoom-in"
            @click="openZoom(buildUrl(imageRawUrl(msg)))"
            @error="markFailed(imageRawUrl(msg))"
          />
          <div class="mt-[6px]">
            <button
              type="button"
              :disabled="isDownloading(imageRawUrl(msg))"
              @click="handleDownload(imageRawUrl(msg))"
              class="inline-flex items-center bg-transparent border-transparent text-sm text-[#2E2E2E]"
            >
              <span v-if="isDownloading(imageRawUrl(msg))" class="inline-block w-[16px] h-[16px] border-2 border-[#9A9A9A] border-t-transparent rounded-full animate-spin mr-[6px]"></span>
              <img v-else src="@/assets/download_icon.svg" alt="Download" class="w-[20px] mr-[6px]" />
              Descargar
            </button>
          </div>
        </template>
        <span v-else class="inline-block text-sm text-[#9A9A9A]">archivo no disponible</span>
        <div v-if="msg.createdAt" class="text-[11px] text-[#9A9A9A] mt-[4px]">
          {{ formatMessageDate(msg.createdAt) }}
        </div>
      </div>
      <div v-else-if="msg.sender !== 'user' && downloadRawUrl(msg)" class="text-left mb-[20px]">
        <button
          type="button"
          :disabled="isDownloading(downloadRawUrl(msg))"
          @click="handleDownload(downloadRawUrl(msg))"
          class="inline-flex items-center bg-[#F2F2F2] rounded-[15px] px-[16px] py-[10px] text-[#2E2E2E] border-transparent"
        >
          <span v-if="isDownloading(downloadRawUrl(msg))" class="inline-block w-[24px] h-[24px] border-2 border-[#9A9A9A] border-t-transparent rounded-full animate-spin mr-[10px]"></span>
          <img v-else src="@/assets/download_icon.svg" alt="Download" class="w-[24px] mr-[10px]" />
          Descargar
        </button>
        <div v-if="msg.createdAt" class="text-[11px] text-[#9A9A9A] mt-[4px]">
          {{ formatMessageDate(msg.createdAt) }}
        </div>
      </div>
    </div>
    <img v-if="isLoading"
      src="@/assets/loading_dots.gif"
      alt="Loading"
      class="mb-[100px]"
    />

    <!-- Lightbox (teleported to body to escape transformed/overflow ancestors) -->
    <Teleport to="body">
      <div
        v-if="zoomSrc"
        @click="closeZoom"
        class="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-[20px] cursor-zoom-out"
      >
        <img :src="zoomSrc" alt="Gráfico" class="max-w-[90%] max-h-[90%] rounded-[8px]" />
      </div>
    </Teleport>
  </div>
</template>

<script>
import ThumbsDownIcon from './ThumbsDownIcon.vue';
import ThumbsUpIcon from './ThumbsUpIcon.vue';
import ChatInteractive from './ChatInteractive.vue';
import { renderMarkdown } from '../utils/markdown';
import { formatMessageDate } from '../utils/date';

export default {
  props: {
    messages: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    debug: {
      type: Boolean,
      required: true
    },
    apiKey: {
      type: String,
      default: ''
    }
  },
  emits: ['feedback', 'donwloadFile', 'answer'],
  components: {
    ThumbsDownIcon,
    ThumbsUpIcon,
    ChatInteractive
  },
  data() {
    return {
      zoomSrc: null,
      failedImages: [],
      downloading: []
    }
  },
  methods: {
    renderMarkdown,
    formatMessageDate,
    isDownloading(rawUrl) {
      return this.downloading.includes(rawUrl)
    },
    // Fetch the file via the query-authenticated route (?apikey=&download=1) and save it,
    // tracking a per-file loading state so the button can show a spinner.
    async handleDownload(rawUrl) {
      if (!rawUrl || this.downloading.includes(rawUrl)) return
      this.downloading.push(rawUrl)
      try {
        const res = await fetch(this.buildUrl(rawUrl, true), { headers: { apiKey: this.apiKey } })
        if (!res.ok) throw new Error(`download failed: ${res.status}`)
        const blob = await res.blob()
        const filename = rawUrl.split('/').pop() || 'archivo'
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } catch (e) {
        console.error('[download] failed', e)
      } finally {
        this.downloading = this.downloading.filter(u => u !== rawUrl)
      }
    },
    isImageUrl(url) {
      return !!url && /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url)
    },
    // Raw path (server-relative) of an inline image: an explicit chart, or an image file.
    imageRawUrl(msg) {
      if (!msg) return null
      if (msg.chartUrl) return msg.chartUrl
      if (this.isImageUrl(msg.fileUrl)) return msg.fileUrl
      if (this.isImageUrl(msg.url)) return msg.url
      return null
    },
    // Raw path of a non-image downloadable file (e.g. Excel).
    downloadRawUrl(msg) {
      if (!msg) return null
      if (msg.fileUrl && !this.isImageUrl(msg.fileUrl)) return msg.fileUrl
      if (msg.url && !this.isImageUrl(msg.url)) return msg.url
      return null
    },
    // Absolute URL with query auth (the file route can't read headers).
    buildUrl(rawUrl, download = false) {
      const base = process.env.VUE_APP_API_URL
      const sep = rawUrl.includes('?') ? '&' : '?'
      let src = `${base}${rawUrl}${sep}apikey=${encodeURIComponent(this.apiKey)}`
      if (download) src += '&download=1'
      return src
    },
    isFailed(rawUrl) {
      return this.failedImages.includes(rawUrl)
    },
    markFailed(rawUrl) {
      if (!this.failedImages.includes(rawUrl)) this.failedImages.push(rawUrl)
    },
    openZoom(src) {
      this.zoomSrc = src
    },
    closeZoom() {
      this.zoomSrc = null
    }
  }
}
</script>

<style scoped>
/* Map rendered Markdown (v-html) to the app's typography. :deep() pierces the v-html subtree. */
.markdown-body :deep(p) { margin: 0 0 8px; }
.markdown-body :deep(p:last-child) { margin-bottom: 0; }
.markdown-body :deep(ul),
.markdown-body :deep(ol) { margin: 0 0 8px; padding-left: 20px; }
.markdown-body :deep(ul) { list-style: disc; }
.markdown-body :deep(ol) { list-style: decimal; }
.markdown-body :deep(li) { margin: 2px 0; }
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4) { font-weight: 700; margin: 10px 0 6px; line-height: 1.3; }
.markdown-body :deep(h1) { font-size: 1.3em; }
.markdown-body :deep(h2) { font-size: 1.2em; }
.markdown-body :deep(h3) { font-size: 1.1em; }
.markdown-body :deep(a) { color: #1d4ed8; text-decoration: underline; word-break: break-word; }
.markdown-body :deep(strong) { font-weight: 700; }
.markdown-body :deep(em) { font-style: italic; }
.markdown-body :deep(code) {
  background: #F2F2F2; border-radius: 4px; padding: 1px 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em;
}
.markdown-body :deep(pre) {
  background: #F2F2F2; border-radius: 8px; padding: 12px; overflow-x: auto; margin: 0 0 8px;
}
.markdown-body :deep(pre code) { background: transparent; padding: 0; }
.markdown-body :deep(blockquote) {
  border-left: 3px solid #E5E5E5; padding-left: 10px; color: #6B6B6B; margin: 0 0 8px;
}
/* GFM tables: constrain width and scroll horizontally instead of overflowing the chat. */
.markdown-body :deep(table) {
  display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; margin: 0 0 8px;
}
.markdown-body :deep(th),
.markdown-body :deep(td) { border: 1px solid #E5E5E5; padding: 6px 10px; text-align: left; white-space: nowrap; }
.markdown-body :deep(th) { background: #F7F7F7; font-weight: 700; }
.markdown-body :deep(img) { max-width: 100%; border-radius: 8px; }
.markdown-body :deep(hr) { border: none; border-top: 1px solid #E5E5E5; margin: 10px 0; }
</style>
